<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import Modal from 'dashboard/components/Modal.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  analysis: { type: Object, default: null },
  isLoading: { type: Boolean, default: false },
});

const show = defineModel('show', { type: Boolean, default: false });

const { t } = useI18n();

const inboxes = useMapGetter('inboxes/getInboxes');
const agents = useMapGetter('agents/getAgents');

const filterLabels = computed(() => ({
  start_date: t('REPORT.AI.FILTERS.START_DATE'),
  end_date: t('REPORT.AI.FILTERS.END_DATE'),
  inbox_id: t('REPORT.AI.FILTERS.INBOX'),
  status: t('REPORT.AI.FILTERS.STATUS.LABEL'),
  assignee_id: t('REPORT.AI.FILTERS.AGENT'),
  label: t('REPORT.AI.FILTERS.LABEL'),
}));

const statusValueLabels = computed(() => ({
  open: t('REPORT.AI.FILTERS.STATUS.OPEN'),
  resolved: t('REPORT.AI.FILTERS.STATUS.RESOLVED'),
  pending: t('REPORT.AI.FILTERS.STATUS.PENDING'),
  snoozed: t('REPORT.AI.FILTERS.STATUS.SNOOZED'),
}));

const displayValue = (key, value) => {
  if (key === 'inbox_id') {
    return (
      inboxes.value.find(inbox => inbox.id === Number(value))?.name ?? value
    );
  }
  if (key === 'assignee_id') {
    return (
      agents.value.find(agent => agent.id === Number(value))?.name ?? value
    );
  }
  if (key === 'status') {
    return statusValueLabels.value[value] ?? value;
  }
  return value;
};

const appliedFilters = computed(() =>
  Object.entries(props.analysis?.filters ?? {})
    .filter(([key, value]) => filterLabels.value[key] && value !== '')
    .map(([key, value]) => ({
      key,
      label: `${filterLabels.value[key]}:`,
      value: displayValue(key, value),
    }))
);

const isFailed = computed(() => props.analysis?.status === 'failed');
const isGenerating = computed(() =>
  ['pending', 'processing'].includes(props.analysis?.status)
);

const handleClose = () => {
  show.value = false;
};
</script>

<template>
  <Modal v-model:show="show" size="!w-[52rem]" :on-close="handleClose">
    <div class="flex flex-col gap-6 p-8">
      <h2 class="text-xl font-medium text-n-slate-12">
        {{ analysis?.name }}
      </h2>

      <div class="flex flex-col gap-2">
        <span class="text-xs font-medium uppercase text-n-slate-11">
          {{ t('REPORT.AI.RESULT.FILTERS_TITLE') }}
        </span>
        <div v-if="appliedFilters.length" class="flex flex-wrap gap-2">
          <span
            v-for="filter in appliedFilters"
            :key="filter.key"
            class="inline-flex items-center h-6 gap-1 px-2 text-xs rounded-md bg-n-alpha-2"
          >
            <span class="text-n-slate-11">{{ filter.label }}</span>
            <span class="font-medium text-n-slate-12">{{ filter.value }}</span>
          </span>
        </div>
        <span v-else class="text-sm text-n-slate-11">
          {{ t('REPORT.AI.RESULT.NO_FILTERS') }}
        </span>
      </div>

      <div
        v-if="analysis?.usage && analysis.status === 'ready'"
        class="flex flex-col gap-2 p-4 border rounded-lg border-n-weak bg-n-alpha-1"
      >
        <span class="text-xs font-medium uppercase text-n-slate-11">
          {{ t('REPORT.AI.RESULT.USAGE') }}
        </span>
        <div class="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div>
            <span class="text-xs text-n-slate-11">{{
              t('REPORT.AI.RESULT.MODEL')
            }}</span>
            <span class="block text-sm font-medium text-n-slate-12">{{
              analysis.usage.model || '—'
            }}</span>
          </div>
          <div>
            <span class="text-xs text-n-slate-11">{{
              t('REPORT.AI.RESULT.TOKENS')
            }}</span>
            <span class="block text-sm font-medium text-n-slate-12">{{
              analysis.usage.total_tokens
            }}</span>
          </div>
          <div>
            <span class="text-xs text-n-slate-11">{{
              t('REPORT.AI.RESULT.PROMPT_TOKENS')
            }}</span>
            <span class="block text-sm font-medium text-n-slate-12">{{
              analysis.usage.prompt_tokens
            }}</span>
          </div>
          <div>
            <span class="text-xs text-n-slate-11">{{
              t('REPORT.AI.RESULT.COMPLETION_TOKENS')
            }}</span>
            <span class="block text-sm font-medium text-n-slate-12">{{
              analysis.usage.completion_tokens
            }}</span>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <div class="flex items-center gap-2">
          <span class="text-xs font-medium uppercase text-n-slate-11">
            {{ t('REPORT.AI.RESULT.TITLE') }}
          </span>
          <span
            v-if="analysis?.status === 'pending'"
            class="inline-flex items-center h-5 px-2 text-xs font-medium rounded-md bg-n-amber-9/10 text-n-amber-11"
          >
            {{ t('REPORT.AI.TABLE.STATUS.PENDING') }}
          </span>
        </div>
        <div v-if="isLoading" class="flex justify-center py-12">
          <Spinner />
        </div>
        <div
          v-else-if="isGenerating"
          class="flex flex-col items-center gap-3 py-12 text-center"
          role="status"
        >
          <Spinner />
          <span class="text-sm text-n-slate-11">
            {{ t('REPORT.AI.RESULT.GENERATING') }}
          </span>
        </div>
        <div
          v-else-if="analysis?.result"
          id="ai-analysis-conversations-results"
          v-dompurify-html="analysis.result"
          class="p-4 border rounded-xl border-n-weak bg-n-background"
          :class="
            isFailed
              ? 'text-sm !text-n-ruby-11'
              : 'max-w-none prose prose-sm !text-n-slate-12'
          "
        />
        <span v-else class="text-sm text-n-slate-11">
          {{ t('REPORT.AI.RESULT.EMPTY') }}
        </span>
      </div>
    </div>
  </Modal>
</template>

<style>
#ai-analysis-conversations-results * {
  color: rgb(var(--slate-12) / var(--tw-text-opacity)) !important;
}
#ai-analysis-conversations-results * {
  word-break: break-all;
}
</style>
